import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { WindowService } from 'src/shared/providers/window.service';
import { Util } from 'src/shared/util/util';

import { ESignDetailPage } from 'src/pages/e-sign/e-sign-detail/e-sign-detail';

import { ParseDateTimePipe } from 'src/shared/pipes/parse-date.pipe';

import { DemoBlockService } from 'src/shared/services/demo-block.service';
import { FeatureGateService } from 'src/shared/services/feature-gate.service';
import { FirmaService, FirmaEnvelope, FirmaTemplate, FirmaStatus } from 'src/shared/services/firma.service';

@Component({
  templateUrl: 'e-sign.page.html',
  styleUrls: ['./e-sign.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ESignPage implements OnInit {
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private navCtrl = inject(CustomNavController);
  private rightSidePanelService = inject(RightSidePanelService);
  private storage = inject(TidyStorage);
  private util = inject(Util);
  private firma = inject(FirmaService);
  private demoBlock = inject(DemoBlockService);
  private featureGate = inject(FeatureGateService);

  readonly windowService = inject(WindowService);

  loaded = false;
  errorMessage: string;
  // Firma isn't wired up yet, so only the demo account has documents/templates —
  // a real account's empty state has to say so rather than read as "nothing sent yet".
  emptyStateText = '';
  private panelClosedSubscription: Subscription;

  tab: 'sent' | 'templates' = 'sent';
  tabTabs = [
    { value: 'sent', label: 'Sent Documents' },
    { value: 'templates', label: 'Templates' },
  ];

  envelopes: FirmaEnvelope[] = [];
  templates: FirmaTemplate[] = [];

  envelopeHeaders: string[] = ['Document', 'Recipient', 'Template', 'Status', 'Sent', 'last-column'];
  envelopeRows: any[] = [];
  templateHeaders: string[] = ['Template', 'Category', 'Description', 'Used', 'Updated', 'last-column'];
  templateRows: any[] = [];

  sendForSignatureAction = () => this.sendForSignature();
  addTemplateAction = () => this.addTemplate();

  async ngOnInit() {
    try {
      await this.loadPage();
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to load e-sign';
      this.util.showError(this.errorMessage, 10000);
    }
  }

  async loadPage() {
    this.emptyStateText = this.demoBlock.isDemo
      ? 'Nothing here yet. Send a document for signature to get started.'
      : 'eSignature is not turned on for your account yet. Contact your account manager to get set up.';
    [this.envelopes, this.templates] = await Promise.all([
      this.firma.listEnvelopes(),
      this.firma.listTemplates(),
    ]);
    this.envelopeRows = this.buildEnvelopeRows();
    this.templateRows = this.buildTemplateRows();
    this.loaded = true;
    this.cdr.markForCheck();
  }

  buildEnvelopeRows(): any[] {
    // Date-only strings ('2026-07-23') — `new Date()` would read them as UTC
    // midnight and render the previous day west of UTC, disagreeing with the
    // detail page for the same document.
    const datePipe = new ParseDateTimePipe();
    return this.envelopes.map(e => ({
      id: e.id,
      'Document': { value: e.name },
      'Recipient': { value: e.recipient_name || '—' },
      'Template': { value: e.template_name },
      'Status': { value: this.statusLabel(e.status) },
      'Sent': { value: e.sent_at ? datePipe.transform(e.sent_at, 'MMM d, yyyy') : '—' },
      'last-column': { value: '', hasChevron: true },
    }));
  }

  buildTemplateRows(): any[] {
    const datePipe = new ParseDateTimePipe();
    return this.templates.map(t => ({
      id: t.id,
      'Template': { value: t.name },
      'Category': { value: t.category },
      'Description': { value: t.description },
      'Used': { value: t.times_used === 1 ? '1 time' : `${t.times_used} times` },
      'Updated': { value: datePipe.transform(t.updated_at, 'MMM d, yyyy') },
      'last-column': { value: '', hasChevron: true },
    }));
  }

  onEnvelopeClick(event: { column: string; element: any }): void {
    const envelope = this.envelopes.find(e => e.id === event.element.id);
    if (envelope) { this.goToPage('e-sign-detail', { kind: 'envelope', envelope }, ESignDetailPage); }
  }

  onTemplateClick(event: { column: string; element: any }): void {
    const template = this.templates.find(t => t.id === event.element.id);
    if (template) { this.goToPage('e-sign-detail', { kind: 'template', template }, ESignDetailPage); }
  }

  selectTab(tab: string | any[]): void {
    this.tab = tab as 'sent' | 'templates';
    this.cdr.markForCheck();
  }

  statusLabel(status: FirmaStatus): string {
    switch (status) {
      case 'not_sent': return 'Not sent';
      case 'in_progress': return 'In progress';
      case 'finished': return 'Signed';
      case 'declined': return 'Declined';
      case 'expired': return 'Expired';
      case 'cancelled': return 'Cancelled';
      default: return status;
    }
  }

  // Sending and template authoring both need the Firma integration, which isn't
  // live for any account yet — so both entry points hand off to the account manager.
  sendForSignature() {
    this.featureGate.gate();
  }

  addTemplate() {
    this.featureGate.gate();
  }

  async goToPage(url: string, params: any, component: any) {
    const isMobileResolution = !this.windowService.isDesktopRes;
    if (!isMobileResolution) {
      const isRightSidePanelAlreadyOpen = await this.storage.retrieve('dialog-right-side-open');
      if (isRightSidePanelAlreadyOpen) {
        this.rightSidePanelService.navigateTo(url, params, component);
      } else {
        this.storage.save('dialog-right-side-open', true);
        this.storage.save('dialog-params', params);
        this.rightSidePanelService.openRightSidePanel(component);
      }
      this.panelClosedSubscription?.unsubscribe();
      this.panelClosedSubscription = this.rightSidePanelService.panelClosed().pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => this.loadPage());
    } else {
      this.navCtrl.navigateForward(url, params);
    }
  }
}
